/**
 * TimeService - Centralized time management utilities
 * 
 * Provides consistent date/time formatting across the application,
 * particularly for generating daily unique IDs for one-per-day submissions.
 */

export const TimeService = {
  /**
   * Generate a daily unique ID combining user ID and date
   * Format: userId_YYYYMMDD (e.g., "user123_20260215")
   * 
   * Used for enforcing one-idea-per-day submission limit
   * 
   * @param {string} userId - Firebase user ID (or 'anonymous')
   * @returns {string} Unique daily ID
   */
  generateDailyId(userId) {
    const now = new Date();
    const yyyy = now.getUTCFullYear();
    const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(now.getUTCDate()).padStart(2, '0');
    return `${userId}_${yyyy}${mm}${dd}`;
  },

  /**
   * Get current UTC date
   * @returns {Date} Current UTC date
   */
  getCurrentUTCDate() {
    return new Date();
  },

  /**
   * Format a Firestore timestamp or Date for display
   * @param {Object|Date} createdAt - Firestore Timestamp or Date object
   * @returns {string} Formatted date string (e.g., "Feb 15, 2026, 03:45 PM")
   */
  formatDateForDisplay(createdAt) {
    try {
      if (createdAt && typeof createdAt.toDate === 'function') {
        // Firestore Timestamp
        return createdAt.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else if (createdAt instanceof Date) {
        // JavaScript Date
        return createdAt.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return 'Date unavailable';
    } catch (err) {
      console.error('TimeService.formatDateForDisplay error:', err);
      return 'Date unavailable';
    }
  }
};

export default TimeService;
